import { useState } from 'react';
import { Edit2, Trash2, Package, AlertTriangle, Plus, Minus } from 'lucide-react';
import useProductStore from '../../stores/useProductStore';

/**
 * Product Card Component
 * عرض بطاقة منتج واحد مع الإجراءات
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Component Structure
 * - P-UX-001: User Experience
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode
 * - P010: Error Handling
 * - P034: Data Validation
 */
const ProductCard = ({ product, onEdit }) => {
  const { deleteProduct, addStock, reduceStock } = useProductStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStockAdjust, setShowStockAdjust] = useState(false);
  const [stockAmount, setStockAmount] = useState(1);
  const [adjustType, setAdjustType] = useState('add'); // 'add' or 'reduce'
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Check stock status
  const stockStatus = product.stock_status || (
    product.stock === 0 ? 'out' :
    product.stock < product.minStock ? 'low' :
    'normal'
  );

  const isLowStock = stockStatus === 'low';
  const isOutOfStock = stockStatus === 'out';
  const isSlowMoving = stockStatus === 'slow';

  // Get stock status config
  const getStockStatusConfig = () => {
    const configs = {
      normal: {
        label: 'طبيعي',
        bgClass: 'bg-green-500',
        textClass: 'text-green-700 dark:text-green-300'
      },
      low: {
        label: 'قريب من النفاد',
        bgClass: 'bg-yellow-500',
        textClass: 'text-yellow-700 dark:text-yellow-300'
      },
      out: {
        label: 'نفد المخزون',
        bgClass: 'bg-red-500',
        textClass: 'text-red-700 dark:text-red-300'
      },
      slow: {
        label: 'راكد',
        bgClass: 'bg-orange-500',
        textClass: 'text-orange-700 dark:text-orange-300'
      }
    };
    return configs[stockStatus] || configs.normal;
  };

  const statusConfig = getStockStatusConfig();

  // Handle Delete
  const handleDelete = () => {
    try {
      deleteProduct(product.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Handle Stock Adjustment
  const handleStockAdjust = () => {
    try {
      if (adjustType === 'add') {
        addStock(product.id, parseInt(stockAmount));
      } else {
        reduceStock(product.id, parseInt(stockAmount));
      }
      setShowStockAdjust(false);
      setStockAmount(1);
    } catch (error) {
      console.error('Error adjusting stock:', error);
      setErrorMessage(error.message);
      setShowErrorModal(true);
    }
  };

  return (
    <>
      <div className="card hover:shadow-lg transition-shadow">
        {/* Product Image Placeholder */}
        <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-t-lg flex items-center justify-center">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover rounded-t-lg"
            />
          ) : (
            <Package className="w-16 h-16 text-gray-400" />
          )}

          {/* Stock Status Badge */}
          {stockStatus !== 'normal' && (
            <div className={`absolute top-2 left-2 ${statusConfig.bgClass} text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1`}>
              {stockStatus === 'low' || stockStatus === 'slow' ? (
                <AlertTriangle className="w-3 h-3" />
              ) : null}
              {statusConfig.label}
            </div>
          )}

          {/* Category Badge */}
          <div className="absolute top-2 right-2 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 text-xs font-medium px-2 py-1 rounded">
            {product.category}
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          {/* Name and Code */}
          <div className="mb-3">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">
              {product.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              كود: {product.code}
            </p>
          </div>

          {/* Price and Cost */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">السعر</p>
              <p className="font-bold text-green-700 dark:text-green-400">
                {product.price.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">التكلفة</p>
              <p className="font-bold text-blue-700 dark:text-blue-400">
                {product.cost.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} د.ك
              </p>
            </div>
          </div>

          {/* Stock Info */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  المخزون
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bgClass} text-white`}>
                  {statusConfig.label}
                </span>
              </div>
              <span
                className={`font-bold text-lg ${
                  isOutOfStock
                    ? 'text-red-600 dark:text-red-400'
                    : isLowStock || isSlowMoving
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {product.stock} {product.unit}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">
                الحد الأدنى: {product.minStock}
              </span>
              <button
                onClick={() => setShowStockAdjust(true)}
                className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                تعديل المخزون
              </button>
            </div>
            {/* Last Sale Date for Slow Moving */}
            {isSlowMoving && product.last_sale_date && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                آخر بيع: {new Date(product.last_sale_date).toLocaleDateString('ar-KW')}
              </div>
            )}
          </div>

          {/* Profit Margin */}
          <div className="flex items-center justify-between mb-3 text-sm">
            <span className="text-gray-600 dark:text-gray-400">هامش الربح</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {((product.price - product.cost) / product.cost * 100).toFixed(1)}%
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(product)}
              className="flex-1 btn btn-secondary flex items-center justify-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              تعديل
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 px-4"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              تأكيد الحذف
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              هل أنت متأكد من حذف المنتج "{product.name}"؟ هذا الإجراء لا يمكن التراجع عنه.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 btn bg-red-600 text-white hover:bg-red-700"
              >
                نعم، احذف
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn btn-secondary"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showStockAdjust && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              تعديل المخزون
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                المخزون الحالي: <span className="font-bold">{product.stock} {product.unit}</span>
              </p>
            </div>

            {/* Adjust Type */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setAdjustType('add')}
                className={`flex-1 btn ${
                  adjustType === 'add'
                    ? 'btn-primary'
                    : 'btn-secondary'
                } flex items-center justify-center gap-2`}
              >
                <Plus className="w-4 h-4" />
                إضافة
              </button>
              <button
                onClick={() => setAdjustType('reduce')}
                className={`flex-1 btn ${
                  adjustType === 'reduce'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'btn-secondary'
                } flex items-center justify-center gap-2`}
              >
                <Minus className="w-4 h-4" />
                تقليل
              </button>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الكمية
              </label>
              <input
                type="number"
                min="1"
                value={stockAmount}
                onChange={(e) => setStockAmount(e.target.value)}
                className="input w-full"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                المخزون بعد التعديل:{' '}
                <span className="font-bold">
                  {adjustType === 'add'
                    ? product.stock + parseInt(stockAmount || 0)
                    : product.stock - parseInt(stockAmount || 0)}{' '}
                  {product.unit}
                </span>
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleStockAdjust}
                className="flex-1 btn btn-primary"
              >
                تأكيد
              </button>
              <button
                onClick={() => {
                  setShowStockAdjust(false);
                  setStockAmount(1);
                }}
                className="flex-1 btn btn-secondary"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">خطأ في التعديل</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 text-lg text-center mb-6">
                {errorMessage}
              </p>

              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
